/**
 * React Query Context Configuration
 * 
 * Creates and configures the QueryClient for TanStack Query
 */

import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { AxiosError } from 'axios';
import type { ErrorResponse } from './types';
import { friendlyHttpMessage } from '@/lib/http/apiError';
import { isAbortError } from '@/lib/org-scope';

let _queryClient: QueryClient | null = null;

/**
 * Global error handler for React Query
 * Handles both query and mutation errors
 */
const AUTH_REDIRECT_MESSAGES = new Set(['Unauthenticated', 'Unauthorized', 'Salesforce session expired']);

const onError = (error: unknown, query: { meta?: Record<string, unknown> }): void => {
  if (query.meta?.silent) return;
  // Cancelled on purpose (the org changed, see getOrgAbortSignal): nothing failed.
  if (isAbortError(error)) return;
  console.error('Fetching Error', error);

  // Extract error message from axios error response.
  //
  // This was the THIRD copy of error-message extraction in the app, and the one that undid the
  // other two: it read `data.message || data.error` but never FastAPI's `detail` — the shape
  // every proxied Python service and `ProxyController.authorize_llm_budget/2` use. Worse, the
  // `if (response?.data)` branch was taken whenever a body existed, so it never fell through to
  // `error.message`, discarding the friendly string the axios interceptor had just written
  // there. Every proxied error therefore surfaced as "An unexpected error occurred" —
  // documentation reads included, since reading an already-generated doc re-issues the POST.
  const axiosError = error as AxiosError<ErrorResponse>;

  let errorMessage: string;
  if (axiosError.response) {
    errorMessage = friendlyHttpMessage({
      status: axiosError.response.status,
      body: axiosError.response.data,
      code: axiosError.code,
    });
  } else if (error instanceof Error && error.message) {
    // No response: an `ApiError` from the shared fetch helper (message already mapped), a
    // GraphQL rejection ("Unauthenticated"), or a plain thrown Error ("No organization selected").
    errorMessage = error.message;
  } else {
    errorMessage = 'An unexpected error occurred';
  }

  // Auth redirect errors already show their own toast via clearSessionAndRedirectToLogin
  if (AUTH_REDIRECT_MESSAGES.has(errorMessage)) return;

  // Show error toast
  toast.error(errorMessage, {
    duration: 4000,
    position: 'top-right',
    icon: '❌',
  });
};

/**
 * Create and configure QueryClient instance
 */
export function getQueryClient(): QueryClient {
  if (_queryClient) {
    return _queryClient;
  }
  _queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => onError(error, query),
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => onError(error, mutation),
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        retry: 2,
        staleTime: 60 * 1000, // 1 minute
        // Keep data in memory for 30 min even when no component is using it,
        // so navigating away briefly and coming back is instant. Each hook
        // can still set its own staleTime to control re-fetching behavior.
        gcTime: 30 * 60 * 1000,
      },
      mutations: {
        // A cancelled request stays cancelled: retrying it would redo work for an org
        // the user has left.
        retry: (failureCount, error) => failureCount < 1 && !isAbortError(error),
      },
    },
  });

  return _queryClient;
}
